import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import classNames from 'classnames';
import InputType from 'common/src/app/data/enum/InputType';
import GroupSearchFieldNames from 'common/src/app/data/enum/FieldNames/GroupSearchFieldNames';
import { hasGeolocationGroupSearch } from 'common/src/app/config/market/market.configdefinitions';
import GroupSearchType from 'common/src/app/data/enum/GroupSearchType';
import IconName from 'common/src/app/data/enum/IconName';
import withDeviceState from 'common/src/app/util/device-state/withDeviceState';
import { DeviceState } from 'common/src/app/data/MediaQueries';
import { detectGroupSearchType } from 'common/src/app/util/detectGroupSearchType';
import { getFormattedPlaceName } from 'common/src/app/util/placesUtils';

import Icon from '../../atoms/Icon';
import TextNew, { Colors, ElementTypes } from '../../atoms/TextNew';
import FormikError from '../../atoms/FormikError';
import Button from '../../atoms/Button';
import Input, { descriptorTypes } from '../../atoms/Input';
import NearestGroupGeoLocation from '../NearestGroupGeoLocation';
import DropDownResults from '../DropDownResults';

import './free-text-group-search.scss';

const FreeTextGroupSearch = (
  {
    handleSubmit,
    handleChange,
    values,
    setFieldValue,
    setErrors,
    errors,
    goToPlaces,
    geoLocationError,
    deviceState,
    showSearchSubmit,
    localeId,
    getPlaces,
  },
  { getMessage },
) => {
  const EMPTY_ARRAY = [];

  const [places, setPlaces] = useState(EMPTY_ARRAY);

  const entry = values[GroupSearchFieldNames.FREE_TEXT]?.trim();

  const [searchType, setSearchType] = useState(GroupSearchType.UNKNOWN);

  const [isLoadingGeo, setIsLoadingGeo] = useState(false);

  const loadingCallBack = isLoading => {
    setIsLoadingGeo(isLoading);
  };

  useEffect(() => {
    if (geoLocationError) {
      setIsLoadingGeo(false);
    }
  }, [geoLocationError]);

  /**
   * Listens for text entry changes:
   * - to re-evaluate the search type
   * - reset 'no town' result
   *   (to re-evaluate wether query matches any result on deletion of characters)
   */
  useEffect(() => {
    let isMounted = true;

    const timeoutId = setTimeout(() => {
      if (!isMounted) return;

      if (!entry || entry.length < 2) {
        setSearchType(GroupSearchType.UNKNOWN);
        return;
      }

      let groupSearchType = detectGroupSearchType(entry);

      // Retry once if the returned type is unknown
      if (groupSearchType === GroupSearchType.UNKNOWN) {
        groupSearchType = detectGroupSearchType(entry);
      }

      setSearchType(groupSearchType);
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [entry]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setErrors({});
      setFieldValue('searchType', searchType);

      if (searchType === GroupSearchType.PLACE) {
        const result = await getPlaces(entry, setErrors);

        if (isMounted) {
          setPlaces(result);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      fetchData();
    }, 100);

    return () => {
      isMounted = false; // prevent state updates
      clearTimeout(timeoutId);
    };
  }, [searchType, setFieldValue, setErrors, getPlaces, entry]);

  function stateReducer(state, changes) {
    // this overwrites downshifts default behaviour of reseting the input value
    // on blur to last selected item or resetting and instead maintains what
    // they have typed
    switch (changes.type) {
      case Downshift.stateChangeTypes.touchEnd:
      case Downshift.stateChangeTypes.mouseUp:
      case Downshift.stateChangeTypes.blurInput:
        return {
          ...changes,
          inputValue: values.postcode,
        };
      default:
        return changes;
    }
  }

  // Used for places - selection:
  // - called when the user has selected one of the suggested drop down places.
  const handlePlaceChange = place => {
    const placeName = getFormattedPlaceName(place);
    goToPlaces(place?.latitude, place?.longitude, placeName);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={classNames(
        showSearchSubmit && 'show-search-submit',
        'molecule-free-text-group-search',
      )}
    >
      <Downshift
        stateReducer={stateReducer}
        onChange={item => handlePlaceChange(item)}
        itemToString={item => (item ? item.value : '')}
      >
        {({
          getRootProps,
          getInputProps,
          getItemProps,
          getMenuProps,
          getLabelProps,
          highlightedIndex,
          selectedItem,
          isOpen,
        }) => (
          <div className="inputWrapper" {...getRootProps({}, { suppressRefError: true })}>
            <div className="inputs">
              {hasGeolocationGroupSearch && (
                <NearestGroupGeoLocation loadingCallBack={loadingCallBack} />
              )}
              <Input
                dataTestid="free-text-group-search-input"
                autocomplete="off"
                convertOnChange={false}
                type={InputType.TEXT}
                {...getInputProps({
                  name: GroupSearchFieldNames.FREE_TEXT,
                  onChange: handleChange,
                  placeholder: getMessage(`${localeId}.searchPlaceholder`),
                })}
                descriptorType={descriptorTypes.GHOST}
                labelProps={{ ...getLabelProps() }}
              />
              <Button
                dataTestid="free-text-group-search-button"
                type={InputType.SUBMIT}
                cid="find-group-button"
                isLoading={isLoadingGeo}
              >
                {showSearchSubmit && deviceState <= DeviceState.SM ? (
                  <Icon name={IconName.MAGNIFY_GLASS} />
                ) : (
                  <TextNew.Sans.SM
                    color={Colors.PRIMARY_LIGHT}
                    element={ElementTypes.SPAN}
                    localeId={`${localeId}.button`}
                  />
                )}
              </Button>
            </div>

            <FormikError
              dataTestid="free-text-group-search-error"
              name={GroupSearchFieldNames.FREE_TEXT}
              triggerUntouched
            />

            {Object.keys(errors).length === 0 &&
              isOpen &&
              searchType === GroupSearchType.PLACE &&
              places?.length > 0 &&
              Array.isArray(places) && (
                <DropDownResults
                  highlightedIndex={highlightedIndex}
                  selectedItem={selectedItem}
                  getMenuProps={getMenuProps}
                  entry={entry}
                  getItemProps={getItemProps}
                  setFieldValue={setFieldValue}
                  setErrors={setErrors}
                  places={places}
                />
              )}
          </div>
        )}
      </Downshift>
    </form>
  );
};

FreeTextGroupSearch.defaultProps = {
  localeId: 'nearestGroupSearchBar',
};

FreeTextGroupSearch.propTypes = {
  localeId: PropTypes.string,
  showSearchSubmit: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object,
  geoLocationError: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  goToPlaces: PropTypes.func,
  deviceState: PropTypes.number,
  getPlaces: PropTypes.func.isRequired,
};

FreeTextGroupSearch.contextTypes = {
  getMessage: PropTypes.func,
};

export default withDeviceState()(FreeTextGroupSearch);
